import { FormikProvider, useFormik } from 'formik';
import { t } from 'i18next';

import { useLocalize } from '@treams/treams-hooks';
import {
	Goal,
	GoalOwnerType,
	useArchiveGoalMutation,
	useCreateGoalMutation,
	usePartialUpdateGoalMutation,
} from '@treams/treams-network';
import { Button, Modal, Popover } from '@treams/treams-ui';

import validator from '../../packages/kakapo-ui-services/factories/ValidatorFactory';
import EditGoalFormFields, { FormValues } from './EditGoalFormFields';

export default function EditGoalModal({
	onClose,
	onSave,
	user,
	goal,
}: {
	onClose: () => void;
	onSave: () => void;
	goal?: Goal | null;
	user: { id: string };
}) {
	const [createGoal] = useCreateGoalMutation();
	const [updateGoal] = usePartialUpdateGoalMutation();
	const [archiveGoal, { loading: archiveIsLoading }] = useArchiveGoalMutation();
	const { language } = useLocalize();

	const formik = useFormik<FormValues>({
		initialValues: {
			id: goal?.id ?? undefined,
			title: goal?.title ?? [{ languageID: language.id, text: '' }],
			dueDate: goal?.dueDate ?? undefined,
			competency: goal
				? {
						competency: goal.competency?.competency,
						competencyLevelIndex: goal.competency?.competencyLevelIndex ?? 0,
					}
				: undefined,
		},
		validationSchema: validator.object({
			id: validator.string().optional(),
			validator: validator.number().optional(),
			title: validator.localizedString(),
			dueDate: validator.date().optional(),
			competency: validator.object({
				competency: validator.object().required(),
				competencyLevelIndex: validator.number().required(),
			}),
		}),
		onSubmit: async (values) => {
			const input = {
				id: values.id!,
				title: values.title,
				dueDate: values.dueDate || null,
				competency: {
					competencyID: values.competency!.competency!.id!,
					competencyLevelIndex: values.competency!.competencyLevelIndex!,
				},
				ownerID: user.id,
				ownerType: GoalOwnerType.User,
			};

			if (values.id) {
				// Filtering out owner
				// eslint-disable-next-line @typescript-eslint/no-unused-vars
				const { ownerID, ownerType, ...withoutOwner } = input;
				const response = await updateGoal({
					variables: {
						input: {
							...withoutOwner,
						},
					},
				});

				if (response.data?.partialUpdateGoal) {
					onSave();
				}
			} else {
				const response = await createGoal({
					variables: {
						input: {
							...input,
						},
					},
				});

				if (response.data?.createGoal) {
					onSave();
				}
			}
		},
	});

	const handleArchive = async () => {
		if (!goal || !goal.id || archiveIsLoading) {
			return;
		}

		const response = await archiveGoal({
			variables: {
				input: {
					id: goal.id,
				},
			},
		});

		if (response.data?.archiveGoal) {
			onSave();
		}
	};

	const editExistingGoal = Boolean(goal?.id);

	const modalTitle = editExistingGoal
		? t('developmentPlan.editGoal.modal.title')
		: t('developmentPlan.addGoal.modal.title');
	const submitText = editExistingGoal
		? t('developmentPlan.editGoal.saveButton')
		: t('developmentPlan.addGoal.addButton');

	return (
		<Popover.Target
			visible
			center
			onDismiss={onClose}
			contents={
				<Modal>
					<FormikProvider value={formik}>
						<Modal.Header title={modalTitle} />
						<Modal.Contents>
							<EditGoalFormFields />
						</Modal.Contents>
						<Modal.FormFooter.Formik
							onCancel={onClose}
							saveTitle={submitText}
							disabled={!formik.isValid || formik.isSubmitting}
						>
							{editExistingGoal && (
								<Button
									display={'inline-block'}
									type="destructive"
									loading={archiveIsLoading}
									title={t('developmentPlan.editGoal.archiveButton')}
									onClick={handleArchive}
								/>
							)}
						</Modal.FormFooter.Formik>
					</FormikProvider>
				</Modal>
			}
		></Popover.Target>
	);
}
